import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { money, when } from "@/lib/rides";
import { AddCredit, Ledger, Stat, useWallet } from "@/components/CreditWallet";
import { PP_LABEL } from "@/components/booking/CheckoutPanel";
import { MyProfileCard } from "@/components/Profiles";
import { PushToggle } from "@/components/PushToggle";
import { MessagesButton } from "@/components/RideMessages";
import { useAuth } from "@/context/AuthContext";
import { dayTime, useMyProfile, useMyRides } from "@/lib/rider";
import { SectionTitle } from "@/components/rider/Nav";
import { Button } from "@/components/ui/button";

function Owed() {
  const { data } = useQuery({ queryKey: ["myPayments"], queryFn: () => api.get("/me/payments").then((r) => r.data) });
  const due = (data || []).filter((p) => p.status === "due");
  return (
    <section className="space-y-2" data-testid="owed-section" id="owed">
      <SectionTitle>Amount owed</SectionTitle>
      {!due.length && <p className="text-sm text-muted-foreground" data-testid="owed-empty">You don't owe anything right now.</p>}
      {due.map((p) => (
        <div key={p.id} className="flex justify-between rounded-xl border border-amber-400/30 px-3 py-2 text-sm" data-testid={`owed-${p.id}`}>
          <span>Post-Payment · due {when(p.due_date)}</span><span className="font-semibold">{money(p.amount)}</span>
        </div>
      ))}
    </section>
  );
}

export function CreditScreen() {
  const qc = useQueryClient();
  const { data: w } = useWallet();
  if (!w) return null;
  const cancel = async (id) => {
    await api.post(`/credit/purchases/${id}/cancel`).catch((e) => toast.error(formatApiError(e)));
    qc.invalidateQueries({ queryKey: ["credit"] });
  };
  return (
    <div className="space-y-5" data-testid="credit-screen">
      <h1 className="font-display text-2xl font-bold tracking-tight">Ride Credit</h1>
      <div className="rounded-2xl border border-primary/40 bg-card p-4">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Available balance</p>
        <p className="font-display text-4xl font-bold text-primary" data-testid="credit-screen-available">{money(w.available)}</p>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <Stat label="Reserved" value={w.reserved} testid="credit-reserved" />
        <Stat label="Used" value={w.used} testid="credit-used" />
        <Stat label="Purchased" value={w.purchased} testid="credit-purchased" />
      </div>
      {w.pending_purchases.map((p) => (
        <div key={p.id} className="flex items-center justify-between rounded-xl border border-amber-400/40 px-3 py-2 text-xs" data-testid="credit-pending-purchase">
          <span>{money(p.amount)} purchase · waiting for Scoopz to confirm payment</span>
          <button type="button" className="font-semibold underline" onClick={() => cancel(p.id)} data-testid={`credit-cancel-purchase-${p.id}`}>Cancel</button>
        </div>
      ))}
      <AddCredit presets={w.presets} onDone={() => qc.invalidateQueries({ queryKey: ["credit"] })} />
      <Owed />
      <section className="space-y-2"><SectionTitle>Transaction history</SectionTitle><Ledger rows={w.ledger} /></section>
    </div>
  );
}

export function NotificationsList() {
  const { data: notes } = useQuery({ queryKey: ["notifications"], queryFn: () => api.get("/notifications").then((r) => r.data) });
  return (
    <section className="space-y-2" data-testid="notifications-list">
      <SectionTitle>Notifications</SectionTitle>
      {!notes?.items?.length && <p className="text-sm text-muted-foreground">No notifications yet.</p>}
      {notes?.items?.map((n) => (
        <div key={n.id} className={`rounded-xl border px-3 py-2 ${n.read ? "border-white/5" : "border-primary/40"}`}>
          <p className="text-sm font-semibold">{n.title}</p><p className="text-xs text-muted-foreground">{n.body}</p>
          <p className="mt-1 text-[10px] text-muted-foreground">{when(n.created_at)}</p>
        </div>
      ))}
    </section>
  );
}

export function MessagesScreen() {
  const { data: rides } = useMyRides();
  const chats = (rides || []).filter((r) => ["pending", "confirmed", "assigned", "en_route", "arrived", "in_progress"].includes(r.status));
  return (
    <div className="space-y-5" data-testid="messages-screen">
      <h1 className="font-display text-2xl font-bold tracking-tight">Messages</h1>
      <section className="space-y-2">
        <SectionTitle>Ride conversations</SectionTitle>
        {!chats.length && <p className="text-sm text-muted-foreground">Messages open for your upcoming and active rides.</p>}
        {chats.map((r) => {
          const { day, time } = dayTime(r.scheduled_at);
          return (
            <div key={r.id} className="flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-card p-3" data-testid={`chat-row-${r.id}`}>
              <div className="min-w-0"><p className="truncate text-sm font-semibold">{r.pickup.name} → {r.dropoff.name}</p><p className="text-xs text-muted-foreground">{day} · {time}</p></div>
              <MessagesButton rideId={r.id} className="h-8 shrink-0 text-xs" label="Open" />
            </div>
          );
        })}
      </section>
      <NotificationsList />
    </div>
  );
}

function PostPayment() {
  const qc = useQueryClient();
  const { data: p } = useMyProfile();
  const status = p?.post_payment_status;
  const apply = async () => {
    try {
      await api.post("/post-payment/apply");
      toast.success("Post-Payment application sent to Scoopz Admin");
      qc.invalidateQueries({ queryKey: ["myProfile"] });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <div className="flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-card p-4" data-testid="profile-post-payment">
      <div><p className="text-sm font-semibold">Post-Payment</p><p className="text-xs text-muted-foreground" data-testid="post-payment-status">{status ? PP_LABEL[status] : "Not applied. Subject to Admin approval."}</p></div>
      {!status && <Button size="sm" variant="outline" className="rounded-full" onClick={apply} data-testid="apply-post-payment-button">Apply</Button>}
    </div>
  );
}

export function ProfileScreen() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="space-y-4" data-testid="profile-screen">
      <h1 className="font-display text-2xl font-bold tracking-tight">Profile</h1>
      <MyProfileCard mode="rider" />
      <div className="space-y-1 rounded-2xl border border-white/10 bg-card p-4 text-sm" data-testid="profile-account">
        <p className="font-semibold">{user?.name}</p><p className="text-muted-foreground">{user?.email}</p>{user?.phone && <p className="text-muted-foreground">{user.phone}</p>}
      </div>
      <PostPayment />
      <div className="rounded-2xl border border-white/10 bg-card p-4"><PushToggle /></div>
      <Button variant="outline" className="h-11 w-full rounded-full" onClick={async () => { await logout(); navigate("/login"); }} data-testid="profile-logout-button">
        <LogOut className="mr-2 h-4 w-4" /> Log out
      </Button>
    </div>
  );
}
